import fs from "node:fs"
import path from "node:path"

import { describeSetupState } from "../orchestrator.js"
import { promptConfirmation } from "../prompts.js"
import { getPreviousSetupState } from "../state.js"
import { SupernovaConfig } from "../../../types/config.js"
import { SetupContext, SetupSession, SetupSessionSchema, SetupSessionStatus, SetupState } from "./types.js"

const persistedContextKeys = [
  "componentPackages",
  "contextName",
  "createdFeatureId",
  "createdProjectId",
  "customPrompt",
  "dependencyPackageNames",
  "dependencyMode",
  "privateDependencyEntries",
  "projectUrl",
  "selectedPromptPreset",
  "repoPaths",
  "scanSourcePath",
  "sharedContextId",
  "shouldRunScan",
  "shouldUploadScanResults",
  "templateDestinationPath",
  "templateFramework",
  "workspaceId",
] as const satisfies (keyof SetupContext)[]

// TODO(Artem): move this to home?
function setupSessionDirectoryPath(): string {
  return path.resolve(".supernova")
}

function setupSessionFilePath(): string {
  return path.join(setupSessionDirectoryPath(), "setup-session.json")
}

function readPersistedSetupSession(): SetupSession | null {
  const sessionFilePath = setupSessionFilePath()
  if (!fs.existsSync(sessionFilePath)) {
    return null
  }

  try {
    const sessionString = fs.readFileSync(sessionFilePath, "utf8")
    const sessionJson = JSON.parse(sessionString)
    const sessionParseResult = SetupSessionSchema.safeParse(sessionJson)
    return sessionParseResult.success ? sessionParseResult.data : null
  } catch {
    return null
  }
}

export async function resolveInitialSetupState(input: { restart: boolean }): Promise<SetupState> {
  if (input.restart) {
    clearPersistedSetupSession()
    return "INIT"
  }

  const persistedSession = readPersistedSetupSession()
  if (!persistedSession || persistedSession.status !== "InProgress") {
    return "INIT"
  }

  if (persistedSession.lastState === "INIT" || persistedSession.lastState === "SELECT_DESIGN_SYSTEM") {
    clearPersistedSetupSession()
    return "INIT"
  }

  const shouldResume = await promptConfirmation(
    `Found unfinished setup while ${describeSetupState(persistedSession.lastState).toLowerCase()}. Resume?`,
  )

  if (!shouldResume) {
    clearPersistedSetupSession()
    return "INIT"
  }

  return getPreviousSetupState(persistedSession.lastState)
}

export function resolveInitialSetupContext(config: SupernovaConfig | null): SetupContext {
  const setup = readPersistedSetupSession()
  const context = Object.fromEntries(persistedContextKeys.map(key => [key, setup?.[key]]))

  return {
    ...context,
    designSystemId: setup?.designSystemId ?? config?.designSystemId,
  } satisfies SetupContext
}

export function persistSetupSession(input: {
  context: SetupContext
  state: SetupState
  status: SetupSessionStatus
}): void {
  const now = new Date().toISOString()
  const existingSession = readPersistedSetupSession()
  const startedAt = existingSession?.startedAt ?? now
  const persistedContext = Object.fromEntries(
    persistedContextKeys.map(key => [key, input.context[key]]),
  ) satisfies SetupContext

  const sessionDirectoryPath = setupSessionDirectoryPath()
  const sessionFilePath = setupSessionFilePath()

  fs.mkdirSync(sessionDirectoryPath, { recursive: true })
  fs.writeFileSync(
    sessionFilePath,
    `${JSON.stringify(
      {
        ...persistedContext,
        designSystemId: input.context.designSystemId,
        lastState: input.state,
        startedAt,
        status: input.status,
        updatedAt: now,
      } satisfies SetupSession,
      null,
      2,
    )}\n`,
  )
}

export function clearPersistedSetupSession(): void {
  fs.rmSync(setupSessionFilePath(), { force: true })
}
